import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Camera, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ProfileDialog({ open, onClose, onSaved }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(user?.photo_url || "");
  const [pay, setPay] = useState({ banco: "", agencia: "", conta: "", tipo_conta: "Corrente", titular: "", cpf_titular: "", pix_chave: "" });
  const [loadingPay, setLoadingPay] = useState(false);
  const [savingPay, setSavingPay] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPhotoUrl(user?.photo_url || "");
    setLoadingPay(true);
    api.get("/profile/payment-info").then(r => {
      const d = r.data || {};
      setPay({
        banco: d.banco || "", agencia: d.agencia || "", conta: d.conta || "",
        tipo_conta: d.tipo_conta || "Corrente", titular: d.titular || user?.name || "",
        cpf_titular: d.cpf_titular || "", pix_chave: d.pix_chave || "",
      });
    }).finally(() => setLoadingPay(false));
  }, [open, user]);

  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecione uma imagem"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem > 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/files/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include", body: fd,
      });
      if (!r.ok) throw new Error("upload");
      const data = await r.json();
      const url = `${API}/files/${data.id}/download`;
      await api.put("/profile/photo", { photo_url: url });
      setPhotoUrl(url);
      toast.success("Foto atualizada");
      onSaved?.();
    } catch { toast.error("Falha ao enviar foto"); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    await api.put("/profile/photo", { photo_url: "" });
    setPhotoUrl("");
    onSaved?.();
    toast.success("Foto removida");
  };

  const savePay = async () => {
    setSavingPay(true);
    try {
      await api.put("/profile/payment-info", pay);
      toast.success("Dados de pagamento salvos");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSavingPay(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Meu Perfil</DialogTitle>
          <DialogDescription>Foto de perfil e dados de pagamento.</DialogDescription>
        </DialogHeader>

        {/* Photo */}
        <div className="flex items-center gap-4 py-2">
          <Avatar className="h-20 w-20 ring-2 ring-[hsl(var(--primary)/0.3)]">
            {photoUrl ? <AvatarImage src={photoUrl} /> : null}
            <AvatarFallback className="text-2xl bg-primary text-primary-foreground">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} data-testid="profile-photo-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-photo-upload">
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
              {photoUrl ? "Trocar foto" : "Enviar foto"}
            </Button>
            {photoUrl && (
              <Button size="sm" variant="ghost" onClick={removePhoto} className="text-red-600" data-testid="profile-photo-remove">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Remover
              </Button>
            )}
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Dados de pagamento</div>
          {loadingPay ? <p className="text-xs text-muted-foreground">Carregando…</p> : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Titular</Label><Input data-testid="pay-titular" value={pay.titular} onChange={(e) => setPay({ ...pay, titular: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">CPF do titular</Label><Input data-testid="pay-cpf" value={pay.cpf_titular} onChange={(e) => setPay({ ...pay, cpf_titular: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Banco</Label><Input data-testid="pay-banco" value={pay.banco} onChange={(e) => setPay({ ...pay, banco: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Tipo</Label>
                  <Select value={pay.tipo_conta} onValueChange={(v) => setPay({ ...pay, tipo_conta: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Corrente">Corrente</SelectItem><SelectItem value="Poupança">Poupança</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Agência</Label><Input data-testid="pay-agencia" value={pay.agencia} onChange={(e) => setPay({ ...pay, agencia: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Conta</Label><Input data-testid="pay-conta" value={pay.conta} onChange={(e) => setPay({ ...pay, conta: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs font-semibold">Chave PIX</Label><Input data-testid="pay-pix" value={pay.pix_chave} onChange={(e) => setPay({ ...pay, pix_chave: e.target.value })} className="mt-1" placeholder="CPF, e-mail, telefone ou aleatória" /></div>
              <p className="text-[10px] text-muted-foreground">Você pode salvar esses dados a qualquer momento. Eles serão usados ao solicitar royalties ou pagamentos.</p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          <Button onClick={savePay} disabled={savingPay} data-testid="profile-pay-save">
            {savingPay ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Salvar dados de pagamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
